import type { ComponentType } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

type ModalProps = {
	[key: string]: boolean;
};

type EditorModalContextType = {
	showEditorModal: (modalName: string) => void;
	hideEditorModal: (modalName: string) => void;
	modal: ModalProps;
};

type EditorModalContextProviderProps = {
	children: React.ReactNode;
};

export const EditorModalContext: React.Context<EditorModalContextType> =
	React.createContext<EditorModalContextType>({
		showEditorModal: () => {},
		hideEditorModal: () => {},
		modal: {},
	});

export const EditorModalContextProvider = ({
	children,
}: EditorModalContextProviderProps): React.JSX.Element => {
	const [modal, setModal] = useState<ModalProps>({});

	const showEditorModal = useCallback(
		(modalName: string): void => {
			setModal({
				...modal,
				[modalName]: true,
			});
		},
		[modal],
	);

	const hideEditorModal = useCallback(
		(modalName: string): void => {
			setModal({
				...modal,
				[modalName]: false,
			});
		},
		[modal],
	);

	const editorModalContextValue = useMemo(
		() => ({ showEditorModal, hideEditorModal, modal }),
		[showEditorModal, hideEditorModal, modal],
	);

	return (
		<EditorModalContext.Provider value={editorModalContextValue}>
			{children}
		</EditorModalContext.Provider>
	);
};

export function withEditorModalContext<T>(WrappedComponent: ComponentType<T>) {
	return (props: any): React.JSX.Element => (
		<EditorModalContextProvider>
			<WrappedComponent {...props} />
		</EditorModalContextProvider>
	);
}

export const useEditorModalContext = (): EditorModalContextType =>
	React.useContext(EditorModalContext);
